import {
  ticketConstants,
  LoadTicketMode,
} from 'store/constants/ticket.constants';
import { ticketService } from 'store/services/ticket.service';
import { alertsActions } from 'store/actions/alerts.actions';
import { fileActions } from 'store/actions/file.actions';
import { workdeskFilterActions } from 'store/actions/workdesk-filter.actions';
import { workdeskTicketsActions } from 'store/actions/workdesk-tickets.actions';
import { TicketDataFromMainPage, Ticket } from 'store/models/Ticket';
import { FormattedMainFile, MainFiles } from 'store/models/MainFiles';
import { curBookingsActions } from 'store/actions/cur-bookings.actions';
import { curBookingsBookingsSelector } from 'store/selectors/cur-bookings.selector';
import { commonPagesSelector } from 'store/selectors/cur-bookings-attachments.selector';
import {
  originalFormTicketSelector,
  ticketDataSelector,
  ticketFormTicketSelector,
  ticketPreviewParamsSubmittedSelector,
  ticketPreviewResultSelector,
} from 'store/selectors/ticket.selector';

import { ticketsMapSelector } from 'store/selectors/workdesk-tickets.selector';
import { userDataAsAssigneeSelector } from 'store/selectors/user.selector';
import { EachWorkdeskTicketI } from 'store/models/workdesk-tickets/each-workdesk-ticket.interface';
import { BookingInfo } from 'store/models/BookingInfo';
import BookingsModel from 'store/models/bookings/Bookings';
import getErrorMessage from 'utils/getErrorMessage';
import { Statuses } from 'store/models/Statuses';
import { HistoryHelper } from 'store/helpers/HistoryHelper';
import {
  ticketAttachmentsRecycledAttachmentPartIdsSelector,
  ticketAttachmentsSelector,
} from 'store/selectors/ticket-attachment.selector';
import { isEqual, differenceWith } from 'lodash';
import { BookingPreviewsHelper } from 'store/models/BookingPreviews';

const LOAD_TICKET_INTERVAL = 3000;
const WAIT_AFTER_TRIGGER_PREPROCESS = 5000;
const MAX_LOAD_COUNT = 4;

let loadTicketTimer: any = null;
let nthLoad = 1;

const updateTicketSi = (siStatus: boolean) => {
  return {
    type: ticketConstants.UPDATE_SI,
    payload: {
      si: siStatus,
    },
  };
};

const updateAttachments = (
  attachments,
  recycledAttachmentPartIds,
) => {
  return {
    type: ticketConstants.UPDATE_ATTACHMENTS,
    payload: {
      attachments,
      recycledAttachmentPartIds,
    },
  };
};

/**
 * used in onload ticket
 * @returns
 */
const cacheOnLoadFormToAutosaveApiData = () => {
  return (dispatch, getState) => {
    const state = getState();
    const formTicket = ticketFormTicketSelector(state); // current form ticket
    if (!formTicket) return;
    const curBookings = curBookingsBookingsSelector(state);
    const recycledAttachmentPartIds = ticketAttachmentsRecycledAttachmentPartIdsSelector(
      state,
    );
    const formattedTicket: TicketDataFromMainPage | null = originalFormTicketSelector(
      state,
    ); // from the api
    const commonPages = commonPagesSelector(state);
    const onLoadFormToAutosaveApi = Ticket.formToAutosaveApi(
      {
        ...formTicket,
        bookings: curBookings,
        commonPages,
      },
      formattedTicket,
      recycledAttachmentPartIds,
    );
    if (!onLoadFormToAutosaveApi) return;
    const {
      data: onLoadFormToAutosaveApiData,
    } = onLoadFormToAutosaveApi;
    dispatch({
      type: ticketConstants.CACHE_ON_LOAD_FORM_TO_AUTOSAVE_API_DATA,
      payload: { onLoadFormToAutosaveApiData },
    });
  };
};

const cacheOnLoadIsUnassigned = (onLoadIsUnassigned: boolean) => {
  return {
    type: ticketConstants.CACHE_ON_LOAD_IS_UNASSIGNED,
    payload: { onLoadIsUnassigned },
  };
};

// TODO: check the nth load and loadTicket timer, might be wrong

const loadTicket = (
  ticketId: string,
  mode: LoadTicketMode,
  fromCE?: boolean,
  inbox?: string | null,
) => {
  console.info(`*** loadTicket mode: ${mode}`);
  if (mode === 'INTERVAL') {
    console.info(
      `*** LOAD_TICKET_INTERVAL: ${LOAD_TICKET_INTERVAL} ms.`,
    );
  }
  // CEParams
  // TODO: check if should use mode in the request ()
  const request = () => {
    return {
      type: ticketConstants.LOAD_TICKET_REQUEST,
      payload: { mode }, // if mode is 'AFTER_UPLOAD_FILE' shouldn't change the isLoading
    };
  };
  const success = (data) => {
    // format autoSave here -> if autoSave.data is string then makes it object
    const { autoSave } = data;
    const dataToUse =
      typeof autoSave.data === 'string'
        ? { ...data, autoSave: { ...data.autoSave, data: {} } }
        : data;
    dataToUse['attachments'] = MainFiles.getSortedAttachments(
      dataToUse.attachments,
    );
    return {
      type: ticketConstants.LOAD_TICKET_SUCCESS,
      payload: { data: dataToUse },
    };
  };
  const failure = (error) => {
    return {
      type: ticketConstants.LOAD_TICKET_ERROR,
      payload: { error },
    };
  };

  return (dispatch, getState) => {
    dispatch(request());
    // // clear on unmount already
    // if (mode === 'INIT') {
    //   // clear the curBookings
    //   dispatch(curBookingsActions.reset());
    // }
    ticketService
      .loadTicket(ticketId)
      .then(async (resDataWithAssignee) => {
        // if status is not ready and is fromCE and has inbox trigger preprocess
        if (mode === 'INIT') {
          const status = resDataWithAssignee.ticket.status;
          const notReady = !Statuses.isReadyToViewStatus(status);
          if (fromCE && inbox) {
            if (notReady) {
              dispatch(triggerPreprocess(ticketId, inbox));
              return;
            } else {
              HistoryHelper.replaceQueryParamsWithoutReload();
            }
          }
        }

        // format here for now to be the expected frontend format
        // flatten ticket out
        const resData = {
          ...resDataWithAssignee.ticket,
          assignee: resDataWithAssignee.assignee
            ? {
                ...resDataWithAssignee.assignee,
                address: resDataWithAssignee.assignee.email,
              }
            : null,
          adminAccess: resDataWithAssignee.adminAccess,
        };
        nthLoad += 1;
        console.info('**** data status ', resData && resData.status);
        if (
          resData &&
          !Statuses.isReadyToViewStatus(resData.status) && // keep loading if it's not ready to view
          nthLoad <= MAX_LOAD_COUNT
        ) {
          loadTicketTimer = setTimeout(() => {
            dispatch(loadTicket(ticketId, 'INTERVAL'));
          }, LOAD_TICKET_INTERVAL);
        } else {
          console.info(`----- DONE loading ticket ----`);
          if (loadTicketTimer) clearTimeout(loadTicketTimer);

          let data = resData;
          let newNames;

          if (mode === 'AFTER_UPLOAD_FILE') {
            // TODO: sort data.attachments with the getState().uploadingFiles
            const { uploadingFiles } = getState();

            if (uploadingFiles && uploadingFiles.length) {
              newNames = uploadingFiles.map((f) => f.filename);
              console.debug(
                `AFTER_UPLOAD_FILE uploadingFiles: `,
                uploadingFiles,
                'newNames',
                newNames,
              );
              // the newly uploaded should be at the ends (not sorted yet)
              const sortedAll = MainFiles.getSortedAttachments(
                data.attachments,
              );
              dispatch(
                updateAttachments(
                  sortedAll,
                  data.autoSave?.data?.recycledAttachmentPartIds,
                ),
              );
            }
          } else {
            // not AFTER_UPLOAD_FILE mode
            await dispatch(success(data)); // await to make sure that the ticket's booking nos is set in the ticket's data first (because initCurBookings in the next step read from the redux store)
          }

          if (mode === 'AFTER_UPLOAD_FILE' && newNames) {
            // TODO: dispatch success to clear the item from the uploadingFiles
            // whatever that is not in the old but in the new -> is the new names
            dispatch(fileActions.uploadSuccess(newNames));
          } else {
            // ******* INIT mode ***********
            if (data.autoSave?.data?.bookings) {
              // update cur bookings. setCurBookings. set cur bookings

              dispatch(
                curBookingsActions.initCurBookings(
                  data.autoSave?.data?.bookings,
                  data.attachments,
                ),
              );
            } else {
              // auto use all bookings from email if doens't have auto save
              // have to pass false to not validate bookings (will validate in handleFirstLoad of MainPage)
              dispatch(
                curBookingsActions.useAllBookingsFromEmail(false),
              );
            }
            if (data.autoSave?.data.commonPages) {
              dispatch(
                curBookingsActions.initCommonPages(
                  data.autoSave?.data?.commonPages,
                ),
              );
            }

            dispatch(cacheOnLoadFormToAutosaveApiData());
            dispatch(
              cacheOnLoadIsUnassigned(!data.assignee?.address),
            );

            if (data.status) {
              // toggle workdesk status if is not current for workdesk page
              dispatch(
                workdeskFilterActions.toggleSelectedStatusIfIsNotCurrent(
                  data.status,
                ),
              );
            }

            if (data.si !== undefined) {
              // toggle ticketType filter for workdesk page
              dispatch(
                workdeskFilterActions.selectTicketTypeIfNotCurrentFromSiValue(
                  data.si,
                ),
              );
            }

            if (data.inbox) {
              // cache selected inboxes for workdesk page
              dispatch(
                workdeskFilterActions.cacheBeforeLoadSelectedInboxes(),
              );
              // set selected inboxes for workdesk page
              dispatch(
                workdeskFilterActions.setSelectedInboxes([
                  data.inbox,
                ]),
              );
            }

            dispatch(
              workdeskTicketsActions.mainPageWsRequestUpdateTickets(),
            );

            // note we don't have statusesStartAt in the workdeskFilter store state. not sure if ws update would work properly

            // end INIT mode
          }
        }
      })
      .catch((error) => {
        // if is in si_ready will get message Cannot read property 'inbox' of null. statusCode 404.
        const errorMessage = getErrorMessage(error);
        console.error('errorMessage', errorMessage, 'mode', mode);
        if (
          errorMessage.includes(
            "Cannot read property 'inbox' of null",
          ) ||
          errorMessage.endsWith('does not exist')
        ) {
          if (fromCE && inbox) {
            dispatch(
              ticketActions.triggerPreprocess(ticketId, inbox),
            );
          } else if (mode === 'INTERVAL') {
            // trigger fetching again
            nthLoad += 1;
            console.log(`at nthLoad ${nthLoad}`);
            if (nthLoad <= MAX_LOAD_COUNT) {
              loadTicketTimer = setTimeout(() => {
                dispatch(loadTicket(ticketId, 'INTERVAL'));
              }, LOAD_TICKET_INTERVAL);
            } else {
              console.log(`reached ${MAX_LOAD_COUNT}`);
              const newErrorMessage = 'Failed to load ticket.';
              dispatch(failure(newErrorMessage as string));
              nthLoad = 1;
            }
          }
          //
        } else {
          dispatch(failure(errorMessage as string));
          dispatch(alertsActions.error(errorMessage));
        }
      });
  };
};

const triggerPreprocess = (id: string, inbox: string) => {
  console.info('trigger preprocess');
  const request = () => {
    return {
      type: ticketConstants.TRIGGER_PREPROCESS_REQUEST,
    };
  };
  const success = () => {
    return {
      type: ticketConstants.TRIGGER_PREPROCESS_SUCCESS,
    };
  };
  const failure = (error) => {
    return {
      type: ticketConstants.TRIGGER_PREPROCESS_ERROR,
      payload: { error },
    };
  };
  return async (dispatch, getState) => {
    dispatch(request());
    try {
      await ticketService.preprocessGmail(id, inbox);
      dispatch(success());
      console.log(`wait ${WAIT_AFTER_TRIGGER_PREPROCESS} ms to load`);

      setTimeout(() => {
        dispatch(loadTicket(id, 'INTERVAL'));
      }, WAIT_AFTER_TRIGGER_PREPROCESS);
    } catch (err) {
      const errorMessage = getErrorMessage(err as Error);
      dispatch(failure(errorMessage as string));
      dispatch(alertsActions.error(errorMessage));
    }
  };
};

/**
 * true delete
 * call api
 * file has partId
 * @param file
 */
const deleteFiles = (deletingPartIds: string[]) => {
  const request = () => {
    return {
      type: ticketConstants.DELETE_FILES_REQUEST,
      payload: { deletingPartIds },
    };
  };
  const success = (failedPartIds: string[]) => {
    return {
      type: ticketConstants.DELETE_FILES_SUCCESS,
      payload: { failedPartIds },
    };
  };
  const failure = (error) => {
    return {
      type: ticketConstants.DELETE_FILES_ERROR,
      payload: { error },
    };
  };

  return (dispatch, getState) => {
    const { id } = getState().ticket?.data || {};
    dispatch(request());
    ticketService
      .deleteFiles(deletingPartIds, id)
      .then(async (data) => {
        dispatch(success(data.failed));
        //  update the curBookings fileSelected
        const succeeded = deletingPartIds.filter(
          (id) => !data.failed.includes(id),
        );
        // after delete files, update the curbookings' fileSelected accordingly s
        await dispatch(
          curBookingsActions.removeFilesFromAllBookings(succeeded),
        );
        // auto save otherwise they won't synced (the main files and in the curBookings)
        dispatch(ticketActions.postAutosaveCurrentFormTicket());
      })
      .catch((error) => {
        const errorMessage = getErrorMessage(error);
        dispatch(failure(errorMessage as string));
        dispatch(alertsActions.error(errorMessage));
      });
  };
};

const updateFilesRecycleStatus = (
  deletingPartIds: string[],
  isRecycled?: boolean,
) => {
  const request = () => {
    return {
      type: ticketConstants.UPDATE_FILES_RECYCLE_STATUS_REQUEST,
      payload: { deletingPartIds },
    };
  };
  const success = (failedPartIds: string[]) => {
    return {
      type: ticketConstants.UPDATE_FILES_RECYCLE_STATUS_SUCCESS,
      payload: { failedPartIds, isRecycled },
    };
  };

  return async (dispatch) => {
    dispatch(request());
    dispatch(success([]));
    if (isRecycled) {
      await dispatch(
        curBookingsActions.removeFilesFromAllBookings(
          deletingPartIds,
        ),
      );
    }
    dispatch(ticketActions.postAutosaveCurrentFormTicket());
  };
};

const submitPreviewBookingIfHasChange = (
  beforeFetchingCallback?: Function,
  afterFetchingCallback?: Function,
) => {
  const request = (bookingNos: string[]) => {
    return {
      type: ticketConstants.PREVIEW_BOOKING_REQUEST,
      payload: { bookingNos },
    };
  };
  const success = (
    preCutPreviewParamsSubmitted: object,
    previewResult: object,
  ) => {
    return {
      type: ticketConstants.PREVIEW_BOOKING_SUCCESS,
      payload: {
        previewParamsSubmitted: preCutPreviewParamsSubmitted,
        previewResult,
      },
    };
  };
  const failure = () => {
    return {
      type: ticketConstants.PREVIEW_BOOKING_ERROR,
    };
  };
  // pull info from redux store
  return async (dispatch, getState) => {
    const curBookings = curBookingsBookingsSelector(getState());
    const ticketFromMain = ticketFormTicketSelector(getState());
    if (!ticketFromMain) throw TypeError('ticketFromMain is missing');
    // compare the params with the old submitted params to reduce the changed booking submit to compute
    const preCutPreviewParamsSubmitted = Ticket.formToSubmitBookingApi(
      {
        ...ticketFromMain,
        bookings: curBookings,
      },
      true, // preview
    );
    // check if old params submitted (at the bookings) and new are the same
    const oldPreviewParamsSubmitted = ticketPreviewParamsSubmittedSelector(
      getState(),
    );
    const oldBookingsToSubmit = oldPreviewParamsSubmitted?.bookings;
    const newBookingsToSubmit = preCutPreviewParamsSubmitted.bookings;

    // show the new value not in old
    const newBookingsNotInOld = differenceWith(
      newBookingsToSubmit,
      oldBookingsToSubmit,
      isEqual,
    );
    // this is not working
    const bookingsHasChanged = newBookingsNotInOld.length > 0;
    console.debug(
      'compare bookingsToSubmit: ',
      {
        newBookingsToSubmit,
        oldBookingsToSubmit,
        newBookingsNotInOld,
      },
      'bookingsHasChanged',
      bookingsHasChanged,
    );

    const oldPreviewResult = ticketPreviewResultSelector(getState());
    // if no change do nothing
    // return what needs to be display
    if (!bookingsHasChanged) {
      return oldPreviewResult;
    }

    if (beforeFetchingCallback) beforeFetchingCallback();

    const changedOnlyParamsSubmitted = {
      ...preCutPreviewParamsSubmitted,
      bookings: newBookingsNotInOld,
    };

    const bookingsM = new BookingsModel(curBookings);
    const bookingNos = bookingsM.bookingNos;
    dispatch(request(bookingNos));
    let updatedPreviewResult;
    try {
      // make api call
      const data = await ticketService.submitBooking(
        changedOnlyParamsSubmitted,
      );
      console.debug('data', data);
      const changedOnlyPreviewResult = data?.bookingPreviews ?? {}; // have only the submitted booking
      updatedPreviewResult = BookingPreviewsHelper.getUpdatedPreviewResult(
        oldPreviewResult,
        preCutPreviewParamsSubmitted,
        changedOnlyPreviewResult,
      );
      dispatch(
        success(preCutPreviewParamsSubmitted, updatedPreviewResult),
      );
      // TODO: if only fetch for some booking only has to merge with the current one to return
    } catch (error) {
      // TODO: refactor all these error alert actions
      const errorMessages = getErrorMessage(error);
      dispatch(failure());
      if (Array.isArray(errorMessages)) {
        errorMessages.forEach((err) => {
          dispatch(alertsActions.error(err, true));
        });
      } else {
        dispatch(alertsActions.error(errorMessages, false));
      }
    }
    if (afterFetchingCallback) afterFetchingCallback();
    return updatedPreviewResult;
  };
};

const submitBooking = (
  ticketFromMain: TicketDataFromMainPage,
  submitAnyway?: boolean, // secret: submit anyway if booking is not found or canceled
) => {
  const request = (params) => {
    return {
      type: ticketConstants.SUBMIT_BOOKING_REQUEST,
      payload: { params },
    };
  };
  const success = () => {
    return {
      type: ticketConstants.SUBMIT_BOOKING_SUCCESS,
    };
  };
  const failure = (error) => {
    return {
      type: ticketConstants.SUBMIT_BOOKING_ERROR,
      payload: { error },
    };
  };
  const stopLoading = () => {
    return {
      type: ticketConstants.SUBMIT_BOOKING_STOP_LOADING,
    };
  };
  return async (dispatch, getState): Promise<boolean> => {
    const curBookings = curBookingsBookingsSelector(getState());

    const params = Ticket.formToSubmitBookingApi({
      ...ticketFromMain,
      bookings: curBookings,
    });

    dispatch(request(params));

    const pass = await dispatch(
      curBookingsActions.validateAllCurBookings(submitAnyway),
    );
    if (!pass) {
      dispatch(stopLoading());
      dispatch(alertsActions.error(`Invalid bookings`));
      return false;
    }

    return ticketService
      .submitBooking(params)
      .then((data) => {
        dispatch(success());
        // if not for preview go back to main page
        // update ticket in the workdesk tickets -> move from SI_READY to INDEXING
        const ticketsMap = ticketsMapSelector(getState());
        if (ticketsMap && params.id in ticketsMap) {
          const ticket = {
            ...ticketsMap[params.id],
            status: Ticket.statusAfterSubmit,
            bookingNos: params.bookings.map((b) => b.bookingNo),
          };

          dispatch(
            workdeskTicketsActions.updateTickets(
              [ticket],
              undefined,
              undefined,
              undefined,
              true, // so won't affect the save
            ),
          ); // this has update main ticket data and therefore toggle status to sending

          // toggle auto select sending
          dispatch(
            workdeskFilterActions.toggleSelectedStatusIfIsNotCurrent(
              Ticket.statusAfterSubmit,
            ),
          );
        } else {
          console.error(`id ${params.id} not in ticketsMap`);
        }

        dispatch(alertsActions.success(`Successfully submit ticket`));
        return true;
      })
      .catch((error) => {
        const errorMessages = getErrorMessage(error);
        dispatch(failure(`${errorMessages}`));
        if (Array.isArray(errorMessages)) {
          errorMessages.forEach((err) => {
            dispatch(alertsActions.error(err, true));
          });
        } else {
          dispatch(alertsActions.error(errorMessages, false));
        }
        return false;
      });
  };
};

/**
 * TODO: check about attachments
 */
const postAutosaveCurrentFormTicket = () => {
  return async (dispatch, getState) => {
    const curBookings = curBookingsBookingsSelector(getState());
    const formTicket = ticketFormTicketSelector(getState());
    if (!formTicket) return;
    const formattedTicket = originalFormTicketSelector(getState());
    const commonPages = commonPagesSelector(getState());
    const allAttachments = ticketAttachmentsSelector(getState());
    const recycledAttachmentPartIds: string[] = allAttachments
      .filter((e) => e.isRecycled)
      .map((el) => el.partId);
    const ticketId = formTicket.id;
    if (!ticketId)
      throw Error(`ticketId is missing from ticket.formTicket`);
    const toAutosave = Ticket.formToAutosaveApi(
      {
        ...formTicket,
        bookings: curBookings,
        commonPages,
      },
      formattedTicket,
      recycledAttachmentPartIds,
    );
    if (!toAutosave) console.warn(`no toAutosave`);
    else {
      const { data, changeFlag } = toAutosave;
      console.debug(
        `postAutosaveCurrentFormTicket: `,
        data,
        changeFlag,
      );
      await dispatch(
        ticketActions.saveTicket(ticketId, data, changeFlag, false),
      );
    }
  };
};
const postTimeTracking = async (
  ticketId: string,
  email: string,
  status: string,
  durationInSeconds: number,
  note: string,
  createdBySystem: boolean,
) => {
  const result = await ticketService.postTimeTracking({
    ticketId,
    email,
    status,
    durationInSeconds,
    note,
    createdBySystem,
  });
  return result;
};
const patchTimeTracking = (
  id: string,
  durationInSeconds: number,
  status: string,
  updatedBySystem: boolean,
) => {
  const result = ticketService.patchTimeTracking({
    id,
    durationInSeconds,
    status,
    updatedBySystem,
  });
  return result;
};
const postGetTimeTracking = async (
  ticketId: string,
  email: string,
) => {
  const result = await ticketService.postGetTimeTracking({
    ticketId,
    email,
  });
  return result;
};
const postAutoUnassignTimeTracking = async (
  ticketId: string,
  durationInSeconds: number,
  note: string,
  email: string,
) => {
  const result = await ticketService.postAutoUnassignTimeTracking({
    ticketId,
    durationInSeconds,
    note,
    email,
  });
  return result;
};

const saveTicket = (
  ticketId,
  toAutosave,
  changeFlag,
  validate = false,
) => {
  const request = () => {
    return {
      type: ticketConstants.SAVE_TICKET_REQUEST,
    };
  };
  const success = () => {
    return {
      type: ticketConstants.SAVE_TICKET_SUCCESS,
      payload: {
        autoSaveData: toAutosave,
      },
    };
  };
  const failure = (errorMessage: string) => {
    return {
      type: ticketConstants.SAVE_TICKET_ERROR,
      payload: {
        error: errorMessage,
      },
    };
  };
  return async (dispatch): Promise<void> => {
    dispatch(request());
    if (validate) {
      await dispatch(curBookingsActions.validateAllCurBookings());
    }
    try {
      await ticketService.saveTicket(
        ticketId,
        toAutosave,
        changeFlag,
      );
      dispatch(success());
      // if change flag is true, should update the workdesk ticket as well
      // TODO: recheck the bookingNos
      if (changeFlag) {
        dispatch(
          workdeskTicketsActions.mergeTicketsToTicketsMap([
            {
              id: ticketId,
              bookingNo: toAutosave.bookingNo,
              bookingNos: toAutosave.bookingNos,
              siKind: toAutosave.siKind,
              urgency: toAutosave.urgency,
            },
          ]),
        );
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      dispatch(failure(errorMessage as string));
      dispatch(alertsActions.error(errorMessage));
    }
  };
};

/**
 * TODO: consider use merge data
 * @param newAtt
 */
const updateTicketFile = (attachment: FormattedMainFile) => {
  return {
    type: ticketConstants.UPDATE_TICKET_ATT,
    payload: { attachment },
  };
};
const updateTicketReplaceFiles = (
  attachments: FormattedMainFile[],
) => {
  return {
    type: ticketConstants.UPDATE_TICKET_ATTACHMENTS,
    payload: { attachments },
  };
};

const updateFormTicketAtKey = (key, value) => {
  const _update = (key, value) => {
    return {
      type: ticketConstants.UPDATE_FORM_TICKET_AT_KEY,
      payload: { key, value },
    };
  };
  return async (dispatch, getState) => {
    const formTicket = ticketFormTicketSelector(getState());
    if (formTicket?.hasOwnProperty(key)) {
      // check if the value is the same as before
      const oldValue = formTicket[key];
      if (oldValue === value) {
        console.debug(
          'new value to update formTicket is the same as the old value so do not proceed',
        );
        return;
      }
    }
    dispatch(_update(key, value));
    if (key === 'bookingNo') {
      const bookings = curBookingsBookingsSelector(getState());
      if (!bookings || bookings.length === 0) {
        await dispatch(curBookingsActions.addNewBookingNo(value));
      }
      // update bottom with top left
      dispatch(updateBottomBookingNoWithTopLeft(value));
      // NOTE: clearing top left after for multi booking -> if want this, uncomment below (and add async before dispatch above)
      // const isMulti = isMultiBookingSelector(getState());
      // if (isMulti) {
      //   dispatch(clearTopLeftBookingNo());
      // }
    }
  };
};

const setFormTicket = (formTicket) => {
  return {
    type: ticketConstants.SET_FORM_TICKET,
    payload: { formTicket },
  };
};

/**
 * @param key
 * @param value
 */
// const updateTicketAtKey = (key, value) => {
//   return (dispatch, getState) => {
//     const formTicket = getState().ticket.formTicket;
//     if (!formTicket)
//       throw Error(`formTicket is missing from store's ticket`);
//     let newTicket = {
//       ...formTicket,
//       [key]: value,
//     };

//     dispatch(setFormTicket(newTicket));
//     // TODO: combine with updateFormTicketAtKey
//     if (key === 'bookingNo') {
//       // update bottom with top left
//       dispatch(updateBottomBookingNoWithTopLeft(value));
//     }
//   };
// };

const updateBottomBookingNoWithTopLeft = (value?: string) => {
  return async (dispatch, getState) => {
    const curBookings = curBookingsBookingsSelector(getState());
    if (value === undefined) {
      if (curBookings.length > 0) {
        value = curBookings[0].bookingNo;
      }
    }
    if (!!value) {
      // sync with the bottom. pass false to not sync back to the top again.
      await dispatch(
        curBookingsActions.updateBookingNoAtIndex(0, value, false),
      );
      await dispatch(
        curBookingsActions.validateBookingNoAtIndex(0, true),
      ); // true to checkFoundAndCancel
    }
  };
};

const updateTicketFileStatus = (
  partId: string,
  status: null | 'REQUESTING',
) => {
  if (!partId) throw Error(`need partId`);
  return {
    type: ticketConstants.UPDATE_TICKET_FILE_STATUS,
    payload: { partId, status },
  };
};

const resetTicket = () => {
  return {
    type: ticketConstants.RESET_TICKET,
  };
};

const updateTicketAssigneeToMe = (senderId: string) => {
  return (dispatch, getState) => {
    const assignee = userDataAsAssigneeSelector(getState());
    if (!assignee) {
      console.error(`cound't find user as assignee for some reason`);
    } else {
      dispatch({
        type: ticketConstants.UPDATE_TICKET_ASSIGNEE,
        payload: {
          assignee,
        },
      });
    }
  };
};

const removeTicketAssignee = () => {
  return {
    type: ticketConstants.REMOVE_TICKET_ASSIGNEE,
  };
};

const mergeToTicket = (updatedDetail: any) => {
  return {
    type: ticketConstants.MERGE_TO_TICKET,
    payload: {
      updatedDetail,
    },
  };
};

/**
 *
 * @param workdeskTicket
 */
const updateTicketDataFromWorkdeskTicketInfo = (
  workdeskTicket: EachWorkdeskTicketI,
) => {
  // check if the id match the current ticket
  return (dispatch, getState) => {
    const data = ticketDataSelector(getState());
    if (!data || !data.id) return;
    if (workdeskTicket.id !== data.id) return;
    const keysInWdTicket = Object.keys(data).filter(
      (key) => key in workdeskTicket,
    );
    if (!keysInWdTicket.length) return;
    const updatedDetail = keysInWdTicket.reduce(
      (acc, key) => ({ ...acc, [key]: workdeskTicket[key] }),
      {},
    ) as EachWorkdeskTicketI;
    console.debug(
      `mergeToTicket updateTicketDataFromWorkdeskTicketInfo: `,
      {
        updatedDetail,
        keysInWdTicket,
        ticketData: data,
        workdeskTicket,
      },
    );
    const { bookingNos, bookingNo, ...others } = updatedDetail;
    const toUpdate = {
      ...others,
      autoSave: { bookingNo, bookingNos },
    };
    dispatch(mergeToTicket(toUpdate));

    // need this for when on the MainPage and do assign unassign which would change status
    // set workdesk filter status
    if (updatedDetail.status) {
      dispatch(
        workdeskFilterActions.toggleSelectedStatusIfIsNotCurrent(
          updatedDetail.status,
        ),
      );
    }

    // need this for when on the MainPage and do si<->nsi which would change si
    // set workdesk si
    if (updatedDetail.si !== undefined) {
      dispatch(
        workdeskFilterActions.selectTicketTypeIfNotCurrentFromSiValue(
          updatedDetail.si,
        ),
      );
    }
  };
};

/**
 * clear top left booking number. blank.
 */
const clearTopLeftBookingNo = () => {
  return (dispatch) => {
    // update top left
    dispatch(ticketActions.updateFormTicketAtKey('bookingNo', ''));
  };
};

const applySplitInfoToTicket = (splitInfo?: string) => {
  return (dispatch) => {
    const isSplit = BookingInfo.bookingInfoSplitInfoToIsSplit(
      splitInfo,
    );
    dispatch(updateFormTicketAtKey('isSplit', isSplit));
  };
};

const setAutoMarkIsSplitError = (error: string) => {
  return {
    type: ticketConstants.SET_AUTO_MARK_IS_SPLIT_ERROR_TXT,
    payload: {
      error,
    },
  };
};

const clearAutoMarkIsSplitError = () => {
  return {
    type: ticketConstants.SET_AUTO_MARK_IS_SPLIT_ERROR_TXT,
    payload: {
      error: '',
    },
  };
};

const setCheckingBookingInfo = (value: boolean) => {
  return {
    type: ticketConstants.SET_CHECKING_BOOKING_INFO,
    payload: {
      value,
    },
  };
};

export const ticketActions = {
  updateFilesRecycleStatus,
  loadTicket,
  deleteFiles,
  submitBooking,
  saveTicket,
  updateTicketFile,
  updateTicketReplaceFiles,
  updateTicketFileStatus,
  updateTicketSi,
  updateFormTicketAtKey,
  // updateTicketAtKey,
  setFormTicket,
  postAutosaveCurrentFormTicket,
  resetTicket,
  updateTicketAssigneeToMe,
  removeTicketAssignee,
  updateTicketDataFromWorkdeskTicketInfo,
  clearTopLeftBookingNo,
  updateBottomBookingNoWithTopLeft,
  applySplitInfoToTicket,
  setAutoMarkIsSplitError,
  clearAutoMarkIsSplitError,
  setCheckingBookingInfo,
  triggerPreprocess,
  postTimeTracking,
  patchTimeTracking,
  postGetTimeTracking,
  postAutoUnassignTimeTracking,
  submitPreviewBookingIfHasChange,
};
